/**
 * Kwolly - Hot Spots
 *
 * Responsibilities:
 *   1. Keyboard navigation — Tab cycles to each dot; Arrow keys cycle between dots
 *      within the section, so the hot spots form a logical group.
 *   2. Theme Editor — re-initialise on shopify:section:load and close any open
 *      popover when shopify:block:deselect fires.
 *   3. Ensure only one popover is open at a time within a section (single-open policy).
 *
 * The x-popover custom element (defined in theme.js) already handles:
 *   - open/close on button click
 *   - close on outside click / overlay click
 *   - mobile bottom-sheet positioning + animation
 *   - desktop anchor positioning
 *   - aria-expanded toggling
 *   - focus trapping on mobile
 */

(function () {
  'use strict';

  class KwollyHotSpots extends HTMLElement {
    constructor () {
      super();
      this._dots = [];
      this._onDotClick = this._onDotClick.bind(this);
      this._onKeyDown  = this._onKeyDown.bind(this);
    }

    connectedCallback () {
      // AbortController lets us remove all document-level listeners in one call,
      // preventing accumulation across Theme Editor section reloads.
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._init();

      // Re-initialise when the merchant edits the section in Theme Editor
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target.contains(this)) {
          this._destroy();
          this._init();
        }
      }, { signal });

      // Close open popover when Theme Editor deselects a block
      document.addEventListener('shopify:block:deselect', (event) => {
        if (event.target.contains(this)) {
          this._closeAll();
        }
      }, { signal });
    }

    disconnectedCallback () {
      this._abortController?.abort();
      this._destroy();
    }

    // ─── Initialise ──────────────────────────────────────────────────────────
    _init () {
      this._dots = Array.from(
        this.querySelectorAll('.kwolly-hot-spots__dot')
      );

      this._dots.forEach((dot) => {
        dot.addEventListener('click', this._onDotClick);
      });

      this.addEventListener('keydown', this._onKeyDown);
    }

    _destroy () {
      this._dots.forEach((dot) => {
        dot.removeEventListener('click', this._onDotClick);
      });
      this.removeEventListener('keydown', this._onKeyDown);
      this._dots = [];
    }

    // ─── Single-open policy ──────────────────────────────────────────────────
    _onDotClick (event) {
      const clickedDot = event.currentTarget;
      const willOpen   = clickedDot.getAttribute('aria-expanded') === 'false';

      // Close every OTHER open dot before the x-popover opens this one
      if (willOpen) {
        this._dots.forEach((dot) => {
          if (dot !== clickedDot && dot.getAttribute('aria-expanded') === 'true') {
            this._closeDot(dot);
          }
        });
      }
    }

    _closeDot (dot) {
      const popoverId = dot.getAttribute('aria-controls');
      if (!popoverId) return;

      const popover = document.getElementById(popoverId);
      if (popover && typeof popover.hide === 'function') {
        popover.hide();
      }
    }

    _closeAll () {
      this._dots.forEach((dot) => this._closeDot(dot));
    }

    // ─── Keyboard: arrow keys cycle between dots ──────────────────────────────
    _onKeyDown (event) {
      const isArrowKey = event.key === 'ArrowRight' || event.key === 'ArrowLeft'
                      || event.key === 'ArrowDown'  || event.key === 'ArrowUp';

      if (!isArrowKey) return;

      const activeDot = this._dots.find((dot) => dot === document.activeElement);
      if (!activeDot) return;

      event.preventDefault();

      const currentIndex = this._dots.indexOf(activeDot);
      const total        = this._dots.length;
      let nextIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % total;
      } else {
        nextIndex = (currentIndex - 1 + total) % total;
      }

      this._dots[nextIndex].focus();
    }
  }

  // Register as a custom element so re-initialisation is automatic
  if (!window.customElements.get('kwolly-hot-spots')) {
    window.customElements.define('kwolly-hot-spots', KwollyHotSpots);
  }
}());
